import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns'

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format date relative to now with fallback to absolute date
 */
export function formatRelativeDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  if (isToday(dateObj)) {
    return format(dateObj, 'h:mm a')
  }
  
  if (isYesterday(dateObj)) {
    return 'Yesterday'
  }
  
  // If within last week, show relative time
  const daysDiff = Math.floor((Date.now() - dateObj.getTime()) / (1000 * 60 * 60 * 24))
  if (daysDiff < 7) {
    return formatDistanceToNow(dateObj, { addSuffix: true })
  }
  
  // Otherwise show absolute date
  return format(dateObj, 'MMM d, yyyy')
}

/**
 * Generate avatar URL with initials fallback
 */
export function getAvatarUrl(name: string | null | undefined, avatarUrl?: string | null): string {
  if (avatarUrl) return avatarUrl
  
  // Handle undefined, null, or empty name
  if (!name || typeof name !== 'string' || name.trim() === '') {
    return `https://ui-avatars.com/api/?name=U&background=f97316&color=fff&size=128&font-size=0.6`
  }
  
  // Generate avatar with initials using a service like DiceBear or UI Avatars
  const initials = name
    .trim()
    .split(' ')
    .filter(word => word.length > 0) // Filter out empty strings
    .map(word => word.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase()
  
  // Fallback to 'U' if no valid initials found
  const finalInitials = initials || 'U'
  
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(finalInitials)}&background=f97316&color=fff&size=128&font-size=0.6`
}

